/**
 * Venue Management – venues, sports, courts, price, availability slots, custom blocks.
 * Accessible by global admin and venue manager.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { useGame } from '../contexts/GameContext';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  doc,
  deleteDoc,
  orderBy,
  query,
  where,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getVenueManagementData } from '../api/venueApi';
import { GAMES } from '../constants/games';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../services/auditLogService';
import { saveVenueToAlgolia, deleteVenueFromAlgolia } from '../services/venueAlgoliaService';
import { sendVenueViewed } from '../services/algoliaInsightsService';
import VenueGantt from '../components/VenueGantt';
import VenueSearch from '../components/VenueSearch';
import PlaceAutocomplete, { isPlacesAvailable } from '../components/PlaceAutocomplete';
import VenueEditModalContent from '../components/VenueEditModalContent';
import { getPlaceDetails, buildMapUrlFromPlaceDetails } from '../services/placeDetailsService';
import { useAlert } from '../contexts/AlertContext';
import './VenueManagement.css';

/** 1=Mon .. 7=Sun, matches VenueGantt and Firestore convention */
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TABS = [
  { id: 'search', label: 'Search & claim' },
  { id: 'venues', label: 'My venues' },
  { id: 'schedule', label: 'Schedule' },
];

/** Format address for display: area, city – pincode or fallback */
function formatVenueAddress(venue) {
  if (!venue) return '—';
  const parts = [venue.address, venue.area, venue.city].filter(Boolean);
  const line = parts.join(', ') || '—';
  const pincode = (venue.pincode || '').toString().trim();
  return pincode ? `${line} – ${pincode}` : line;
}

function defaultCourt(venueGameIds) {
  return {
    id: 'court-' + Date.now(),
    name: '',
    gameIds: Array.isArray(venueGameIds) && venueGameIds.length ? [...venueGameIds] : ['table-tennis'],
    price: '',
    priceType: 'per_hour',
    priceUnit: 'per hour',
    availabilitySlots: [],
  };
}

function formatSlot(slot) {
  if (!slot || slot.dayOfWeek == null) return '';
  const day = DAY_NAMES[slot.dayOfWeek - 1] || 'Day ' + slot.dayOfWeek;
  const start = slot.startTime || '';
  const end = slot.endTime || '';
  return `${day} ${start}-${end}`;
}

export default function VenueManagement() {
  const { currentUser, userProfile } = useAuth();
  const { isAdmin, isSuperAdmin } = useRole();
  const { gameId, games: gamesList } = useGame();
  const { showAlert, showConfirmPromise, showToast } = useAlert();
  const gamesForSelect = (gamesList && gamesList.length) ? gamesList : GAMES;
  const [venues, setVenues] = useState([]);
  const [managerEmails, setManagerEmails] = useState({});
  const [venueAdminEmailToAdd, setVenueAdminEmailToAdd] = useState({});
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVenueId, setEditingVenueId] = useState(null);
  const [editingCourtKey, setEditingCourtKey] = useState(null);
  const [newVenueName, setNewVenueName] = useState('');
  const [newVenueAddress, setNewVenueAddress] = useState('');
  const [newVenuePincode, setNewVenuePincode] = useState('');
  const [newVenueCity, setNewVenueCity] = useState('');
  const [newVenueArea, setNewVenueArea] = useState('');
  const [newVenueDescription, setNewVenueDescription] = useState('');
  const [newVenuePhone, setNewVenuePhone] = useState('');
  const [newVenueMapUrl, setNewVenueMapUrl] = useState('');
  const [newVenueFormattedAddress, setNewVenueFormattedAddress] = useState('');
  const [newVenueLatitude, setNewVenueLatitude] = useState('');
  const [newVenueLongitude, setNewVenueLongitude] = useState('');
  const [newVenuePlaceId, setNewVenuePlaceId] = useState('');
  const [newVenueGameIds, setNewVenueGameIds] = useState(['table-tennis']);
  const [editingLocationVenueId, setEditingLocationVenueId] = useState(null);
  const [editingLocationFormattedAddress, setEditingLocationFormattedAddress] = useState('');
  const [editingLocationLatitude, setEditingLocationLatitude] = useState('');
  const [editingLocationLongitude, setEditingLocationLongitude] = useState('');
  const [editingLocationPlaceId, setEditingLocationPlaceId] = useState('');
  const [editingLocationPhone, setEditingLocationPhone] = useState('');
  const [editingLocationWebsite, setEditingLocationWebsite] = useState('');
  const [editingLocationMapUrl, setEditingLocationMapUrl] = useState('');
  const [enhancingLocation, setEnhancingLocation] = useState(false);
  const [venueEditModalVenue, setVenueEditModalVenue] = useState(null);
  const [selectedVenueFromSearch, setSelectedVenueFromSearch] = useState(null);
  const [selectedVenueHasGame, setSelectedVenueHasGame] = useState(true);
  const [courtForm, setCourtForm] = useState(null);
  const [blockVenueId, setBlockVenueId] = useState('');
  const [blockCourtId, setBlockCourtId] = useState('');
  const [blockStart, setBlockStart] = useState('');
  const [blockEnd, setBlockEnd] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [ganttDate, setGanttDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [ganttVenueFilter, setGanttVenueFilter] = useState('');
  const [groups, setGroups] = useState([]);
  const [editingPrivateVenueId, setEditingPrivateVenueId] = useState(null);
  const [editingPrivateIsPrivate, setEditingPrivateIsPrivate] = useState(false);
  const [editingPrivateAllowedGroupIds, setEditingPrivateAllowedGroupIds] = useState([]);
  const [activeTab, setActiveTab] = useState('search');
  const [claimedVenuesFilter, setClaimedVenuesFilter] = useState('');
  const [addVenueFormOpen, setAddVenueFormOpen] = useState(false);
  const [claimSuccessMessage, setClaimSuccessMessage] = useState(null);
  const [courtFormError, setCourtFormError] = useState('');
  const [blockTimeError, setBlockTimeError] = useState('');
  const venueViewedForInsights = useRef(new Set());

  const canManageAll = isAdmin || isSuperAdmin;
  /** Super admin or venue owner (in managerIds) can edit that venue's properties */
  const canEditVenueProperties = (venue) =>
    isSuperAdmin || (venue && (venue.managerIds || []).includes(currentUser?.uid));
  /** Can add/remove blocks if user can edit at least one venue */
  const canManageBlocks = isSuperAdmin || (currentUser && venues.some((v) => (v.managerIds || []).includes(currentUser.uid)));

  const fetchAllVenueData = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getVenueManagementData();
      setVenues(data.venues || []);
      setManagerEmails(data.managerEmails || {});
      setBookings(data.bookings || []);
      setBlocks(data.blocks || []);
      setMatches(data.matches || []);
      setTournaments(data.tournaments || []);
      setGroups(data.groups || []);
    } catch (err) {
      console.error('Error fetching venue data:', err);
      setVenues([]);
      setBookings([]);
      setBlocks([]);
      setMatches([]);
      setTournaments([]);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    fetchAllVenueData();
  }, [currentUser, fetchAllVenueData]);

  async function handleAddVenue(e) {
    e.preventDefault();
    if (!currentUser || !newVenueName.trim()) return;
    setSaving(true);
    try {
      const lat = newVenueLatitude.trim() ? parseFloat(newVenueLatitude, 10) : null;
      const lng = newVenueLongitude.trim() ? parseFloat(newVenueLongitude, 10) : null;
      const payload = {
        name: newVenueName.trim(),
        address: newVenueAddress.trim() || '',
        pincode: (newVenuePincode || '').trim() || null,
        city: (newVenueCity || '').trim() || null,
        area: (newVenueArea || '').trim() || null,
        description: (newVenueDescription || '').trim() || null,
        phone: (newVenuePhone || '').trim() || null,
        mapUrl: (newVenueMapUrl || '').trim() || null,
        formattedAddress: (newVenueFormattedAddress || '').trim() || null,
        latitude: (lat != null && !Number.isNaN(lat)) ? lat : null,
        longitude: (lng != null && !Number.isNaN(lng)) ? lng : null,
        placeId: (newVenuePlaceId || '').trim() || null,
        gameIds: Array.isArray(newVenueGameIds) ? newVenueGameIds : ['table-tennis'],
        courts: [],
        managerIds: [currentUser.uid],
        createdBy: currentUser.uid,
        createdAt: Timestamp.now(),
        isPrivate: false,
        allowedGroupIds: [],
      };
      const ref = await addDoc(collection(db, 'venues'), payload);
      await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.VENUE, ref.id, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, null, { name: newVenueName.trim() });
      await saveVenueToAlgolia({ id: ref.id, ...payload });
      setNewVenueName('');
      setNewVenueAddress('');
      setNewVenuePincode('');
      setNewVenueCity('');
      setNewVenueArea('');
      setNewVenueDescription('');
      setNewVenuePhone('');
      setNewVenueMapUrl('');
      setNewVenueFormattedAddress('');
      setNewVenueLatitude('');
      setNewVenueLongitude('');
      setNewVenuePlaceId('');
      setNewVenueGameIds(['table-tennis']);
      setAddVenueFormOpen(false);
      fetchAllVenueData();
    } catch (err) {
      console.error('Error adding venue:', err);
      showAlert('Error adding venue: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function startAddCourt(venue) {
    setCourtFormError('');
    setEditingVenueId(venue.id);
    setEditingCourtKey('new');
    setCourtForm(defaultCourt(venue.gameIds));
    setCourtForm((prev) => ({ ...prev, name: '' }));
  }

  function startEditCourt(venue, court) {
    setCourtFormError('');
    setEditingVenueId(venue.id);
    setEditingCourtKey(court.id);
    setCourtForm({
      id: court.id,
      name: court.name || '',
      gameIds: Array.isArray(court.gameIds) ? [...court.gameIds] : (venue.gameIds || ['table-tennis']),
      price: court.price != null && court.price !== '' ? String(court.price) : '',
      priceType: court.priceType || 'per_hour',
      priceUnit: court.priceUnit || 'per hour',
      availabilitySlots: Array.isArray(court.availabilitySlots) ? court.availabilitySlots.map((s) => ({ ...s })) : [],
    });
  }

  function addAvailabilitySlotToForm() {
    setCourtForm((prev) => ({
      ...prev,
      availabilitySlots: [...(prev.availabilitySlots || []), { dayOfWeek: 1, startTime: '18:00', endTime: '21:00' }],
    })); // dayOfWeek 1 = Monday
  }

  function removeAvailabilitySlotFromForm(index) {
    setCourtForm((prev) => ({
      ...prev,
      availabilitySlots: prev.availabilitySlots.filter((_, i) => i !== index),
    }));
  }

  function updateCourtFormSlot(index, field, value) {
    setCourtForm((prev) => {
      const slots = [...(prev.availabilitySlots || [])];
      if (!slots[index]) return prev;
      slots[index] = { ...slots[index], [field]: value };
      return { ...prev, availabilitySlots: slots };
    });
  }

  async function saveCourtForm() {
    setCourtFormError('');
    const venue = venues.find((v) => v.id === editingVenueId);
    if (!canEditVenueProperties(venue) || !editingVenueId || !courtForm) return;
    if (!venue) return;
    const name = (courtForm.name || '').trim();
    if (!name) {
      setCourtFormError('Court name is required.');
      return;
    }
    setSaving(true);
    try {
      const courts = [...(venue.courts || [])];
      const payload = {
        id: courtForm.id,
        name,
        gameIds: courtForm.gameIds || venue.gameIds || ['table-tennis'],
        price: (courtForm.price || '').trim(),
        priceType: courtForm.priceType || 'per_hour',
        priceUnit: courtForm.priceUnit || 'per hour',
        availabilitySlots: (courtForm.availabilitySlots || []).filter(
          (s) => s.startTime && s.endTime
        ),
      };
      if (editingCourtKey === 'new') {
        courts.push(payload);
      } else {
        const idx = courts.findIndex((c) => c.id === editingCourtKey);
        if (idx >= 0) courts[idx] = payload;
      }
      await updateDoc(doc(db, 'venues', editingVenueId), { courts });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.COURT, payload.id, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, { price: payload.price, priceType: payload.priceType }, { venueId: editingVenueId });
      const updatedVenue = { ...venue, courts };
      await saveVenueToAlgolia(updatedVenue);
      setEditingVenueId(null);
      setEditingCourtKey(null);
      setCourtForm(null);
      fetchAllVenueData();
    } catch (err) {
      console.error('Error saving court:', err);
      showAlert('Error saving court: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function cancelCourtForm() {
    setEditingVenueId(null);
    setEditingCourtKey(null);
    setCourtForm(null);
  }

  async function handleDeleteCourt(venueId, courtId) {
    const venue = venues.find((v) => v.id === venueId);
    if (!canEditVenueProperties(venue)) return;
    const ok = await showConfirmPromise('Remove this court?', { title: 'Remove court' });
    if (!ok) return;
    if (!venue) return;
    setSaving(true);
    try {
      const courts = (venue.courts || []).filter((c) => c.id !== courtId);
      await updateDoc(doc(db, 'venues', venueId), { courts });
      await logAudit(AUDIT_ACTIONS.DELETE, AUDIT_ENTITIES.COURT, courtId, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, null, { venueId, venueName: venue?.name });
      fetchAllVenueData();
      cancelCourtForm();
    } catch (err) {
      console.error('Error deleting court:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteVenue(venueId) {
    const venue = venues.find((v) => v.id === venueId);
    if (!canEditVenueProperties(venue)) return;
    const ok = await showConfirmPromise('Delete this venue?', { title: 'Delete venue', destructive: true });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'venues', venueId));
      await deleteVenueFromAlgolia(venueId);
      await logAudit(AUDIT_ACTIONS.DELETE, AUDIT_ENTITIES.VENUE, venueId, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, null, { name: venue?.name });
      if (selectedVenueFromSearch?.id === venueId) setSelectedVenueFromSearch(null);
      fetchAllVenueData();
    } catch (err) {
      console.error('Error deleting venue:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    }
  }

  async function handleAddVenueAdmin(venueId) {
    const venue = venues.find((v) => v.id === venueId);
    if (!venue || !canEditVenueProperties(venue)) return;
    const email = (venueAdminEmailToAdd[venueId] || '').trim().toLowerCase();
    if (!email) return;
    setSaving(true);
    try {
      const usersSnap = await getDocs(query(collection(db, 'users'), where('email', '==', email), limit(1)));
      if (usersSnap.empty) {
        showAlert('No user found with that email.', { variant: 'warning' });
        setSaving(false);
        return;
      }
      const uid = usersSnap.docs[0].id;
      const venueToUpdate = venues.find((v) => v.id === venueId);
      const managerIds = [...(venueToUpdate.managerIds || []), uid].filter((id, i, arr) => arr.indexOf(id) === i);
      await updateDoc(doc(db, 'venues', venueId), { managerIds });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.VENUE_ADMIN, venueId, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, { added: uid, email }, { venueName: venueToUpdate?.name });
      const updatedVenue = { ...venueToUpdate, managerIds };
      await saveVenueToAlgolia(updatedVenue);
      setVenueAdminEmailToAdd((p) => ({ ...p, [venueId]: '' }));
      fetchAllVenueData();
    } catch (err) {
      console.error('Error adding venue admin:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveVenueAdmin(venueId, uidToRemove) {
    const venue = venues.find((v) => v.id === venueId);
    if (!venue || !canEditVenueProperties(venue) || uidToRemove === currentUser.uid) return;
    const ok = await showConfirmPromise('Remove this venue admin?', { title: 'Remove admin' });
    if (!ok) return;
    const managerIds = (venue.managerIds || []).filter((id) => id !== uidToRemove);
    if (managerIds.length === 0) {
      showAlert('Venue must have at least one admin.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'venues', venueId), { managerIds });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.VENUE_ADMIN, venueId, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, { removed: uidToRemove }, { venueName: venue?.name });
      await saveVenueToAlgolia({ ...venue, managerIds });
      fetchAllVenueData();
    } catch (err) {
      console.error('Error removing venue admin:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function startEditPrivateVenue(venue) {
    setEditingPrivateVenueId(venue.id);
    setEditingPrivateIsPrivate(!!venue.isPrivate);
    setEditingPrivateAllowedGroupIds(Array.isArray(venue.allowedGroupIds) ? [...venue.allowedGroupIds] : []);
  }

  function cancelEditPrivateVenue() {
    setEditingPrivateVenueId(null);
    setEditingPrivateIsPrivate(false);
    setEditingPrivateAllowedGroupIds([]);
  }

  async function saveVenuePrivateSettings(venueId) {
    const venue = venues.find((v) => v.id === venueId);
    if (!venue || !canEditVenueProperties(venue)) return;
    setSaving(true);
    try {
      const prev = { isPrivate: !!venue.isPrivate, allowedGroupIds: venue.allowedGroupIds || [] };
      const next = { isPrivate: editingPrivateIsPrivate, allowedGroupIds: [...editingPrivateAllowedGroupIds] };
      await updateDoc(doc(db, 'venues', venueId), { isPrivate: next.isPrivate, allowedGroupIds: next.allowedGroupIds });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.VENUE, venueId, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, { before: prev, after: next }, { venueName: venue?.name });
      await saveVenueToAlgolia({ ...venue, ...next });
      cancelEditPrivateVenue();
      fetchAllVenueData();
    } catch (err) {
      console.error('Error saving venue private settings:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function startEditVenueLocation(venue) {
    setEditingLocationVenueId(venue.id);
    setEditingLocationFormattedAddress(venue.formattedAddress || venue.address || '');
    setEditingLocationLatitude(venue.latitude != null ? String(venue.latitude) : '');
    setEditingLocationLongitude(venue.longitude != null ? String(venue.longitude) : '');
    setEditingLocationPlaceId(venue.placeId || '');
    setEditingLocationPhone(venue.phone || '');
    setEditingLocationWebsite(venue.website || '');
    setEditingLocationMapUrl(venue.mapUrl || '');
  }

  function cancelEditVenueLocation() {
    setEditingLocationVenueId(null);
    setEditingLocationFormattedAddress('');
    setEditingLocationLatitude('');
    setEditingLocationLongitude('');
    setEditingLocationPlaceId('');
    setEditingLocationPhone('');
    setEditingLocationWebsite('');
    setEditingLocationMapUrl('');
  }

  function openEditModal(venue) {
    if (!venue) return;
    setVenueEditModalVenue(venue);
    setEditingVenueId(venue.id);
    startEditVenueLocation(venue);
    startEditPrivateVenue(venue);
  }

  function closeEditModal() {
    setVenueEditModalVenue(null);
    setEditingVenueId(null);
    setEditingCourtKey(null);
    setCourtForm(null);
    cancelEditVenueLocation();
    cancelEditPrivateVenue();
  }

  async function saveVenueLocation(venueId) {
    const venue = venues.find((v) => v.id === venueId);
    if (!venue || !canEditVenueProperties(venue)) return;
    const lat = editingLocationLatitude.trim() ? parseFloat(editingLocationLatitude, 10) : null;
    const lng = editingLocationLongitude.trim() ? parseFloat(editingLocationLongitude, 10) : null;
    setSaving(true);
    try {
      const next = {
        formattedAddress: (editingLocationFormattedAddress || '').trim() || null,
        latitude: (lat != null && !Number.isNaN(lat)) ? lat : null,
        longitude: (lng != null && !Number.isNaN(lng)) ? lng : null,
        placeId: (editingLocationPlaceId || '').trim() || null,
        phone: (editingLocationPhone || '').trim() || null,
        website: (editingLocationWebsite || '').trim() || null,
        mapUrl: (editingLocationMapUrl || '').trim() || null,
      };
      await updateDoc(doc(db, 'venues', venueId), next);
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.VENUE, venueId, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, { field: 'location' }, { venueName: venue?.name });
      await saveVenueToAlgolia({ ...venue, ...next });
      cancelEditVenueLocation();
      fetchAllVenueData();
      showToast('Venue updated with location and details.', { variant: 'success' });
    } catch (err) {
      console.error('Error saving venue location:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleAddBlock(e) {
    e.preventDefault();
    setBlockTimeError('');
    if (!canManageBlocks || !blockVenueId || !blockStart || !blockEnd) return;
    const blockStartMs = new Date(blockStart).getTime();
    const blockEndMs = new Date(blockEnd).getTime();
    if (blockEndMs <= blockStartMs) {
      setBlockTimeError('End time must be after start time.');
      return;
    }
    setSaving(true);
    try {
      const ref = await addDoc(collection(db, 'venueBlocks'), {
        venueId: blockVenueId,
        courtId: blockCourtId || null,
        startTime: Timestamp.fromDate(new Date(blockStart)),
        endTime: Timestamp.fromDate(new Date(blockEnd)),
        reason: (blockReason || '').trim() || null,
        createdBy: currentUser.uid,
        createdAt: Timestamp.now(),
      });
      await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.VENUE_BLOCK, ref.id, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, null, { venueId: blockVenueId });
      setBlockVenueId('');
      setBlockCourtId('');
      setBlockStart('');
      setBlockEnd('');
      setBlockReason('');
      setBlockTimeError('');
      fetchAllVenueData();
    } catch (err) {
      console.error('Error adding block:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteBlock(blockId) {
    if (!canManageBlocks) return;
    const ok = await showConfirmPromise('Remove this block?', { title: 'Remove block' });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'venueBlocks', blockId));
      await logAudit(AUDIT_ACTIONS.DELETE, AUDIT_ENTITIES.VENUE_BLOCK, blockId, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, null, null);
      fetchAllVenueData();
    } catch (err) {
      console.error('Error deleting block:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    }
  }

  const venueById = (id) => venues.find((v) => v.id === id);
  const venueByAlgoliaId = (algoliaObjectID) =>
    venues.find((v) => v.id === algoliaObjectID || v.algoliaObjectID === algoliaObjectID);

  /** Build Google Maps URL from place (placeId preferred, else lat,lng). */
  function buildMapUrlFromPlace(place) {
    if (!place) return null;
    const pid = (place.placeId || '').trim();
    if (pid) return `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${encodeURIComponent(pid)}`;
    const lat = place.latitude != null && !Number.isNaN(Number(place.latitude)) ? Number(place.latitude) : null;
    const lng = place.longitude != null && !Number.isNaN(Number(place.longitude)) ? Number(place.longitude) : null;
    if (lat != null && lng != null) return `https://www.google.com/maps?q=${lat},${lng}`;
    return null;
  }

  /** Create a custom venue from a Google Places selection (when Algolia has insufficient results). */
  async function handleCreateCustomVenueFromPlace(place) {
    if (!currentUser || !place) return;
    let formattedAddress = (place.formattedAddress || '').trim() || null;
    let lat = place.latitude != null && !Number.isNaN(Number(place.latitude)) ? Number(place.latitude) : null;
    let lng = place.longitude != null && !Number.isNaN(Number(place.longitude)) ? Number(place.longitude) : null;
    const placeId = (place.placeId || '').trim() || null;
    let mapUrl = buildMapUrlFromPlace(place);
    let phone = null;
    let website = null;
    let name = formattedAddress ? (formattedAddress.split(',')[0] || formattedAddress).trim().slice(0, 80) || 'Custom location' : 'Custom location';
    if (placeId) {
      const details = await getPlaceDetails(placeId);
      if (details) {
        if (details.formattedAddress) formattedAddress = details.formattedAddress;
        if (details.latitude != null) lat = details.latitude;
        if (details.longitude != null) lng = details.longitude;
        if (details.formattedPhoneNumber) phone = details.formattedPhoneNumber;
        if (details.website) website = details.website;
        if (details.name) name = details.name.trim().slice(0, 80) || name;
        const detailsMapUrl = buildMapUrlFromPlaceDetails(details);
        if (detailsMapUrl) mapUrl = detailsMapUrl;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name,
        address: formattedAddress,
        formattedAddress,
        latitude: lat,
        longitude: lng,
        placeId,
        mapUrl,
        phone,
        website,
        pincode: null,
        city: null,
        area: null,
        description: null,
        gameIds: [gameId || 'table-tennis'],
        courts: [],
        managerIds: [currentUser.uid],
        createdBy: currentUser.uid,
        createdAt: Timestamp.now(),
        isPrivate: false,
        allowedGroupIds: [],
      };
      const ref = await addDoc(collection(db, 'venues'), payload);
      await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.VENUE, ref.id, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, null, { name: payload.name, customFromPlace: true });
      const newVenue = { id: ref.id, ...payload };
      await saveVenueToAlgolia(newVenue);
      setVenues((prev) => [...prev, newVenue]);
      setSelectedVenueFromSearch(newVenue);
      setClaimSuccessMessage(`"${payload.name}" created as a custom venue. Add courts and details in My venues.`);
      setActiveTab('venues');
      fetchAllVenueData();
      setTimeout(() => setClaimSuccessMessage(null), 5000);
      showToast('Custom venue created with map location.', { variant: 'success' });
    } catch (err) {
      console.error('Error creating custom venue from place:', err);
      showAlert('Error creating venue: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  /** Filter claimed venues by name, address, area, city */
  const filteredVenues = React.useMemo(() => {
    const q = (claimedVenuesFilter || '').trim().toLowerCase();
    if (!q) return venues;
    return venues.filter((v) => {
      const name = (v.name || '').toLowerCase();
      const address = (v.address || '').toLowerCase();
      const area = (v.area || '').toLowerCase();
      const city = (v.city || '').toLowerCase();
      return name.includes(q) || address.includes(q) || area.includes(q) || city.includes(q);
    });
  }, [venues, claimedVenuesFilter]);


  useEffect(() => {
    if (!selectedVenueFromSearch) return;
    const algoliaId = selectedVenueFromSearch.id || selectedVenueFromSearch.objectID;
    const fullVenue = venues.find((v) => v.id === algoliaId || v.algoliaObjectID === algoliaId) || selectedVenueFromSearch;
    const venueBookingsCount = bookings.filter((b) => b.venueId === fullVenue.id).length;
    const venueMatchesCount = matches.filter((m) => m.venueId === fullVenue.id).length;
    const hasEventsOrBookings = venueBookingsCount > 0 || venueMatchesCount > 0;
    const objectID = fullVenue.id || algoliaId;
    if (hasEventsOrBookings && objectID && !venueViewedForInsights.current.has(objectID)) {
      venueViewedForInsights.current.add(objectID);
      sendVenueViewed({ objectID });
    }
  }, [selectedVenueFromSearch, venues, bookings, matches]);

  async function handleClaimVenue() {
    if (!currentUser || !selectedVenueFromSearch) return;
    const algoliaId = selectedVenueFromSearch.id || selectedVenueFromSearch.objectID;
    if (!algoliaId) return;
    if (venueByAlgoliaId(algoliaId)) {
      showToast('This venue is already claimed and in your list.', { variant: 'info' });
      return;
    }
    setSaving(true);
    try {
      const gameIds = selectedVenueFromSearch.gameIds || selectedVenueFromSearch.gamesSupported || [gameId].filter(Boolean);
      const hitLat = selectedVenueFromSearch.latitude != null ? Number(selectedVenueFromSearch.latitude) : null;
      const hitLng = selectedVenueFromSearch.longitude != null ? Number(selectedVenueFromSearch.longitude) : null;
      const payload = {
        name: (selectedVenueFromSearch.name || '').trim() || 'Unnamed Venue',
        address: (selectedVenueFromSearch.address || '').trim() || null,
        pincode: (selectedVenueFromSearch.pincode || '').toString().trim() || null,
        city: (selectedVenueFromSearch.city || '').trim() || null,
        area: (selectedVenueFromSearch.area || '').trim() || null,
        description: null,
        phone: null,
        mapUrl: selectedVenueFromSearch.mapUrl || null,
        formattedAddress: (selectedVenueFromSearch.formattedAddress || selectedVenueFromSearch.address || '').trim() || null,
        latitude: (hitLat != null && !Number.isNaN(hitLat)) ? hitLat : null,
        longitude: (hitLng != null && !Number.isNaN(hitLng)) ? hitLng : null,
        placeId: (selectedVenueFromSearch.placeId || '').trim() || null,
        gameIds: Array.isArray(gameIds) ? gameIds : [gameId || 'table-tennis'],
        courts: [],
        managerIds: [currentUser.uid],
        createdBy: currentUser.uid,
        createdAt: Timestamp.now(),
        isPrivate: false,
        allowedGroupIds: [],
      };
      await setDoc(doc(db, 'venues', algoliaId), payload);
      await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.VENUE, algoliaId, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: userProfile?.displayName || currentUser.displayName,
      }, null, { name: payload.name, claimed: true });
      const newVenue = { id: algoliaId, ...payload };
      await saveVenueToAlgolia(newVenue);
      setSelectedVenueFromSearch(null);
      setClaimSuccessMessage(`"${payload.name}" is now in your claimed venues.`);
      setActiveTab('venues');
      fetchAllVenueData();
      setTimeout(() => setClaimSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error claiming venue:', err);
      showAlert('Error claiming venue: ' + err.message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  const getCourtName = (venueId, courtId) => {
    const venue = venueById(venueId);
    const court = (venue && venue.courts) ? venue.courts.find((c) => c.id === courtId) : null;
    return court ? court.name : courtId || '—';
  };

  if (loading) {
    return <div className="loading">Loading venues...</div>;
  }

  return (
    <div className="venue-management-container">
      <div className="venue-management-header">
        <h1 className="page-title">Venues</h1>
        <p className="page-subtitle">
          Search for venues, manage claimed venues and courts, and view the schedule (booked via GradedPlay).
        </p>
        <div className="venue-tabs" role="tablist" aria-label="Venue sections">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={activeTab === tab.id ? 'venue-tab active' : 'venue-tab'}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.id === 'venues' ? `${tab.label} (${venues.length})` : tab.label}
            </button>
          ))}
        </div>
      </div>

      {claimSuccessMessage && (
        <div className="venue-success-banner" role="status">
          {claimSuccessMessage}
        </div>
      )}

      <div className="venue-content">
        {activeTab === 'search' && (
          <>
      {/* Part 1: Search for venue */}
      <section className="venue-part venue-part-search card-ui">
        <h2 className="venue-part-title">Search for venue</h2>
            <p className="venue-search-hint">Search by pincode, area, city or venue name. Results are filtered by the current game. Claim a venue to use it in tournaments and matches.</p>
            <div className="venue-search-wrap">
              <VenueSearch
                gameId={gameId}
                firestoreVenues={venues}
                placeholder="e.g. 560001, Koramangala, Bangalore or venue name..."
                className="venue-search-hero"
                onSelect={(venue, opts) => {
                  setSelectedVenueFromSearch(venue || null);
                  setSelectedVenueHasGame(opts?.hasGame !== false);
                }}
                onSelectCustomPlace={handleCreateCustomVenueFromPlace}
              />
            </div>
          </section>

          {selectedVenueFromSearch && (() => {
            const algoliaId = selectedVenueFromSearch.id || selectedVenueFromSearch.objectID;
            const fullVenue = venueByAlgoliaId(algoliaId) || selectedVenueFromSearch;
            const isClaimed = fullVenue.id && venues.some((v) => v.id === fullVenue.id || v.algoliaObjectID === fullVenue.id);
            const venueBookings = bookings.filter((b) => b.venueId === fullVenue.id);
            const venueBlocks = blocks.filter((b) => b.venueId === fullVenue.id);
            const venueMatches = matches.filter((m) => m.venueId === fullVenue.id);
            const courts = fullVenue.courts || [];
            return (
              <div className="venue-availability-card card-ui venue-availability-redesign">
                <div className="venue-availability-header">
                  <div className="venue-availability-header-row">
                    <h3>
                      {fullVenue.name}
                      {(isClaimed || fullVenue.verified) && (
                        <span className="venue-verified-badge" title="Claimed venue – owner/admin can manage pricing, timing and sports">Verified</span>
                      )}
                    </h3>
                    <button
                      type="button"
                      className="venue-clear-selection btn-secondary btn-sm"
                      onClick={() => setSelectedVenueFromSearch(null)}
                      aria-label="Clear selection"
                    >
                      Clear
                    </button>
                  </div>
                  {!isClaimed && currentUser && (
                    <div className="venue-claim-actions">
                      <p className="venue-claim-hint">This venue is from search. Claim it to manage pricing, timing, courts and manual blocks. Tournaments and matches can only use claimed venues.</p>
                      <button type="button" className="btn-primary" onClick={handleClaimVenue} disabled={saving}>
                        {saving ? 'Claiming…' : 'Claim this venue'}
                      </button>
                    </div>
                  )}
                  {!selectedVenueHasGame && (
                    <p className="venue-no-game-notice">This venue has no court for the selected game. You can still view details and claim it, then add a court for this game.</p>
                  )}
                </div>
                <div className="venue-availability-details">
                  <p>
                    <strong>Address:</strong> {formatVenueAddress(fullVenue)}
                    {fullVenue.mapUrl && (
                      <a href={fullVenue.mapUrl} target="_blank" rel="noopener noreferrer" className="venue-map-link">View on map</a>
                    )}
                  </p>
                  <p><strong>Courts:</strong> {courts.length} · <strong>Bookings:</strong> {venueBookings.length} · <strong>Blocks:</strong> {venueBlocks.length} · <strong>Matches:</strong> {venueMatches.length}</p>
                  {courts.length > 0 && (
                    <ul className="venue-availability-courts">
                      {courts.map((c) => (
                        <li key={c.id}>
                          {c.name} – {(c.gameIds || []).join(', ')} · {(c.availabilitySlots || []).map((s) => formatSlot(s)).join('; ') || 'No slots'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="venue-availability-chart">
                  <h4>Availability (day view)</h4>
                  <div className="venue-availability-chart-controls">
                    <label>
                      <span>Date</span>
                      <input
                        type="date"
                        value={ganttDate}
                        onChange={(e) => setGanttDate(e.target.value)}
                      />
                    </label>
                  </div>
                  <VenueGantt
                    venues={[fullVenue]}
                    blocks={venueBlocks}
                    bookings={venueBookings}
                    matches={venueMatches}
                    tournaments={tournaments}
                    selectedDate={ganttDate}
                    venueIdFilter=""
                  />
                  <p className="venue-availability-legend-note">Green = available · Red = blocked · Blue = booking · Purple = tournament match. See Schedule section below for all venues.</p>
                </div>
              </div>
            );
          })()}
          </>
        )}

        {activeTab === 'venues' && (
      <>
      {/* Part 2: Claimed venues */}
      <section className="venue-part venue-part-claimed">
        <h2 className="venue-part-title">Claimed venues</h2>
        <p className="venue-part-desc">Venues you manage. Add courts, admins, and booking access. Add a new venue or claim one from search above.</p>
        {currentUser && (
            <>
            {!addVenueFormOpen ? (
              <button type="button" className="btn-secondary venue-add-venue-toggle" onClick={() => setAddVenueFormOpen(true)}>
                + Add new venue
              </button>
            ) : (
            <form className="venue-form card-ui" onSubmit={handleAddVenue}>
              <h3>Add venue</h3>
              <div className="form-row">
                <label>Name</label>
                <input
                  type="text"
                  value={newVenueName}
                  onChange={(e) => setNewVenueName(e.target.value)}
                  placeholder="e.g. Community Hall"
                  required
                />
              </div>
              <div className="form-row">
                <label>Address</label>
                <input
                  type="text"
                  value={newVenueAddress}
                  onChange={(e) => setNewVenueAddress(e.target.value)}
                  placeholder="Street, locality"
                />
              </div>
              <div className="form-row form-row-inline">
                <div>
                  <label>Pincode</label>
                  <input
                    type="text"
                    value={newVenuePincode}
                    onChange={(e) => setNewVenuePincode(e.target.value)}
                    placeholder="e.g. 560001"
                    maxLength={12}
                  />
                </div>
                <div>
                  <label>City</label>
                  <input
                    type="text"
                    value={newVenueCity}
                    onChange={(e) => setNewVenueCity(e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div>
                  <label>Area / Locality</label>
                  <input
                    type="text"
                    value={newVenueArea}
                    onChange={(e) => setNewVenueArea(e.target.value)}
                    placeholder="Area name"
                  />
                </div>
              </div>
              <div className="form-row">
                <label>Description (optional)</label>
                <textarea
                  value={newVenueDescription}
                  onChange={(e) => setNewVenueDescription(e.target.value)}
                  placeholder="Short description"
                  rows={2}
                  style={{ maxWidth: '400px' }}
                />
              </div>
              <div className="form-row">
                <label>Phone (optional)</label>
                <input
                  type="tel"
                  value={newVenuePhone}
                  onChange={(e) => setNewVenuePhone(e.target.value)}
                  placeholder="e.g. +91 98765 43210"
                  style={{ maxWidth: '280px' }}
                />
              </div>
              <div className="form-row">
                <label>Google Maps link (optional)</label>
                <input
                  type="url"
                  value={newVenueMapUrl}
                  onChange={(e) => setNewVenueMapUrl(e.target.value)}
                  placeholder="https://maps.google.com/…"
                  style={{ maxWidth: '400px' }}
                />
              </div>
              <div className="form-row venue-location-section">
                <label>Map location (for calendar events &amp; maps)</label>
                <p className="venue-location-hint">
                  {isPlacesAvailable() ? 'Search for an address or place; it will fill the address and coordinates.' : 'Full address or coordinates. Used when adding matches to Google Calendar. Set REACT_APP_GOOGLE_MAPS_API_KEY for address search.'}
                </p>
                <PlaceAutocomplete
                  value={newVenueFormattedAddress}
                  onChange={setNewVenueFormattedAddress}
                  onSelect={(place) => {
                    if (place.formattedAddress) setNewVenueFormattedAddress(place.formattedAddress);
                    setNewVenueLatitude(place.latitude != null ? String(place.latitude) : '');
                    setNewVenueLongitude(place.longitude != null ? String(place.longitude) : '');
                    if (place.placeId) setNewVenuePlaceId(place.placeId);
                  }}
                  placeholder="Search address or place…"
                  inputStyle={{ maxWidth: '400px', marginBottom: '8px' }}
                />
                <div className="form-row-inline venue-location-coords">
                  <input
                    type="text"
                    value={newVenueLatitude}
                    onChange={(e) => setNewVenueLatitude(e.target.value)}
                    placeholder="Latitude (optional)"
                    style={{ width: '120px' }}
                  />
                  <input
                    type="text"
                    value={newVenueLongitude}
                    onChange={(e) => setNewVenueLongitude(e.target.value)}
                    placeholder="Longitude (optional)"
                    style={{ width: '120px' }}
                  />
                </div>
              </div>
              <div className="form-row">
                <label>Sports</label>
                <div className="venue-game-chips">
                  {gamesForSelect.map((g) => (
                    <label key={g.id} className="chip-label">
                      <input
                        type="checkbox"
                        checked={(newVenueGameIds || []).includes(g.id)}
                        onChange={(e) => {
                          if (e.target.checked) setNewVenueGameIds([...(newVenueGameIds || []), g.id]);
                          else setNewVenueGameIds((newVenueGameIds || []).filter((id) => id !== g.id));
                        }}
                      />
                      <span>{g.icon} {g.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="venue-form-actions">
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Adding…' : 'Add venue'}
                </button>
                <button type="button" className="btn-secondary" onClick={() => setAddVenueFormOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
            )}
            </>
          )}

          {activeTab === 'venues' && venues.length > 0 && (
            <div className="venue-filter-wrap venue-list-search">
              <label htmlFor="venue-filter-input" className="venue-filter-label">Search my venues</label>
              <input
                id="venue-filter-input"
                type="text"
                className="venue-filter-input"
                placeholder="Search by name, address, area, city..."
                value={claimedVenuesFilter}
                onChange={(e) => setClaimedVenuesFilter(e.target.value)}
              />
            </div>
          )}

          <div className="venues-list venue-list-view">
            {filteredVenues.length === 0 ? (
              <p className="venue-empty">
                {venues.length === 0
                  ? 'No venues yet. Search above to claim a venue or add one manually.'
                  : `No venues match "${claimedVenuesFilter.trim()}". Try a different filter.`}
              </p>
            ) : (
              <table className="venue-list-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Address</th>
                    <th>Courts</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
              {filteredVenues.map((venue) => (
                <tr key={venue.id}>
                  <td><strong>{venue.name}</strong></td>
                  <td>{formatVenueAddress(venue)}</td>
                  <td>{(venue.courts || []).length}</td>
                  <td className="venue-list-actions">
                    <button type="button" className="btn-primary btn-sm" onClick={() => openEditModal(venue)}>Edit</button>
                    {venue.mapUrl && <a href={venue.mapUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary btn-sm">Map</a>}
                    {canEditVenueProperties(venue) && <button type="button" className="btn-destructive btn-sm" onClick={() => handleDeleteVenue(venue.id)}>Delete</button>}
                  </td>
                </tr>
              ))}
                </tbody>
              </table>
            )}
          </div>

          {venueEditModalVenue && (() => {
            const v = venueEditModalVenue;
            return (
          <div className="venue-edit-modal-overlay" onClick={closeEditModal} role="dialog" aria-modal="true" aria-labelledby="venue-edit-modal-title">
            <div className="venue-edit-modal" onClick={(e) => e.stopPropagation()}>
              <div className="venue-edit-modal-header">
                <h2 id="venue-edit-modal-title">Edit venue: {v.name}</h2>
                <button type="button" className="venue-edit-modal-close" onClick={closeEditModal} aria-label="Close">×</button>
              </div>
              <div className="venue-edit-modal-body">
                <VenueEditModalContent
                  venue={v}
                  venueById={venueById}
                  canEditVenueProperties={canEditVenueProperties}
                  managerEmails={managerEmails}
                  venueAdminEmailToAdd={venueAdminEmailToAdd}
                  setVenueAdminEmailToAdd={setVenueAdminEmailToAdd}
                  groups={groups}
                  gamesForSelect={gamesForSelect}
                  saving={saving}
                  editingVenueId={editingVenueId}
                  courtForm={courtForm}
                  setCourtForm={setCourtForm}
                  courtFormError={courtFormError}
                  setCourtFormError={setCourtFormError}
                  editingCourtKey={editingCourtKey}
                  editingLocationVenueId={editingLocationVenueId}
                  editingLocationFormattedAddress={editingLocationFormattedAddress}
                  setEditingLocationFormattedAddress={setEditingLocationFormattedAddress}
                  editingLocationLatitude={editingLocationLatitude}
                  setEditingLocationLatitude={setEditingLocationLatitude}
                  editingLocationLongitude={editingLocationLongitude}
                  setEditingLocationLongitude={setEditingLocationLongitude}
                  editingLocationPlaceId={editingLocationPlaceId}
                  setEditingLocationPlaceId={setEditingLocationPlaceId}
                  editingLocationPhone={editingLocationPhone}
                  setEditingLocationPhone={setEditingLocationPhone}
                  editingLocationWebsite={editingLocationWebsite}
                  setEditingLocationWebsite={setEditingLocationWebsite}
                  editingLocationMapUrl={editingLocationMapUrl}
                  setEditingLocationMapUrl={setEditingLocationMapUrl}
                  enhancingLocation={enhancingLocation}
                  setEnhancingLocation={setEnhancingLocation}
                  editingPrivateVenueId={editingPrivateVenueId}
                  editingPrivateIsPrivate={editingPrivateIsPrivate}
                  setEditingPrivateIsPrivate={setEditingPrivateIsPrivate}
                  editingPrivateAllowedGroupIds={editingPrivateAllowedGroupIds}
                  setEditingPrivateAllowedGroupIds={setEditingPrivateAllowedGroupIds}
                  startEditVenueLocation={startEditVenueLocation}
                  saveVenueLocation={saveVenueLocation}
                  cancelEditVenueLocation={cancelEditVenueLocation}
                  startEditCourt={startEditCourt}
                  startAddCourt={startAddCourt}
                  saveCourtForm={saveCourtForm}
                  cancelCourtForm={cancelCourtForm}
                  handleDeleteCourt={handleDeleteCourt}
                  updateCourtFormSlot={updateCourtFormSlot}
                  addAvailabilitySlotToForm={addAvailabilitySlotToForm}
                  removeAvailabilitySlotFromForm={removeAvailabilitySlotFromForm}
                  startEditPrivateVenue={startEditPrivateVenue}
                  saveVenuePrivateSettings={saveVenuePrivateSettings}
                  cancelEditPrivateVenue={cancelEditPrivateVenue}
                  handleAddVenueAdmin={handleAddVenueAdmin}
                  handleRemoveVenueAdmin={handleRemoveVenueAdmin}
                  handleDeleteVenue={handleDeleteVenue}
                  getPlaceDetails={getPlaceDetails}
                  buildMapUrlFromPlaceDetails={buildMapUrlFromPlaceDetails}
                  DAY_NAMES={DAY_NAMES}
                  formatSlot={formatSlot}
                  formatVenueAddress={formatVenueAddress}
                />
              </div>
            </div>
          </div>
            );
          })()}


        {/* Block schedule (within Claimed venues) */}
        {canManageBlocks && (
          <>
            <h3 className="venue-part-subtitle">Block schedule</h3>
            <p className="venue-part-desc">Only your claimed venues can have manual blocks. Add a block to mark a venue or court as unavailable for a period (e.g. maintenance, private event).</p>
            <form className="block-form card-ui" onSubmit={handleAddBlock}>
              <h4>Add block (custom unavailable period)</h4>
              <div className="form-row">
                <label>Venue</label>
                <select
                  value={blockVenueId}
                  onChange={(e) => { setBlockVenueId(e.target.value); setBlockCourtId(''); }}
                  required
                >
                  <option value="">Select venue</option>
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>Court (optional – leave empty to block whole venue)</label>
                <select
                  value={blockCourtId}
                  onChange={(e) => setBlockCourtId(e.target.value)}
                >
                  <option value="">All courts</option>
                  {((venueById(blockVenueId) || {}).courts || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>Start</label>
                <input type="datetime-local" value={blockStart} onChange={(e) => setBlockStart(e.target.value)} required />
              </div>
              <div className="form-row">
                <label>End</label>
                <input
                  type="datetime-local"
                  value={blockEnd}
                  onChange={(e) => { setBlockEnd(e.target.value); setBlockTimeError(''); }}
                  required
                  aria-invalid={!!blockTimeError}
                />
                {blockTimeError && (
                  <p className="venue-form-error" role="alert">{blockTimeError}</p>
                )}
              </div>
              <div className="form-row">
                <label>Reason (optional)</label>
                <input type="text" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="e.g. Maintenance, Private event" />
              </div>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Adding…' : 'Add block'}
              </button>
            </form>
          </>
        )}
        <div className="blocks-list">
          {blocks.length > 0 && (
            <div className="bookings-table-wrap">
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th>Venue</th>
                    <th>Court</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Reason</th>
                    {canManageBlocks && <th />}
                  </tr>
                </thead>
                <tbody>
                  {blocks.map((b) => (
                    <tr key={b.id}>
                      <td>{(venueById(b.venueId) || {}).name || b.venueId}</td>
                      <td>{b.courtId ? getCourtName(b.venueId, b.courtId) : 'All courts'}</td>
                      <td>{b.startTime && b.startTime.toDate ? b.startTime.toDate().toLocaleString() : (b.startTime && new Date(b.startTime).toLocaleString()) || '—'}</td>
                      <td>{b.endTime && b.endTime.toDate ? b.endTime.toDate().toLocaleString() : (b.endTime && new Date(b.endTime).toLocaleString()) || '—'}</td>
                      <td>{b.reason || '—'}</td>
                      {canManageBlocks && canEditVenueProperties(venueById(b.venueId)) && (
                        <td>
                          <button type="button" className="btn-destructive btn-sm" onClick={() => handleDeleteBlock(b.id)}>Remove</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </section>
      </>
        )}

        {activeTab === 'schedule' && (
      <>
      {/* Part 3: Schedule (Gantt for booked via GradedPlay) */}
      <section className="venue-part venue-part-schedule">
        <h2 className="venue-part-title">Schedule</h2>
        <p className="venue-part-desc">Day view of availability, blocks, venue bookings, and tournament matches. Bookings are created when hosting matches or via other flows—only manual blocks can be added here for your claimed venues.</p>
        <div className="venue-gantt-controls card-ui">
          <h3>Day view</h3>
          <div className="venue-gantt-filters">
            <label>
              <span>Date</span>
              <input
                type="date"
                value={ganttDate}
                onChange={(e) => setGanttDate(e.target.value)}
              />
            </label>
            <label>
              <span>Venue</span>
              <select
                value={ganttVenueFilter}
                onChange={(e) => setGanttVenueFilter(e.target.value)}
              >
                <option value="">All venues</option>
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <VenueGantt
          venues={venues}
          blocks={blocks}
          bookings={bookings}
          matches={matches}
          tournaments={tournaments}
          selectedDate={ganttDate}
          venueIdFilter={ganttVenueFilter}
        />
      </section>
      </>
        )}
      </div>
    </div>
  );
}
